import React, { FunctionComponent, useContext, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import {
  Grid,
  Divider,
  Heading,
  Skeleton,
  Button,
  Text,
  Link,
  Message,
  useModalDeepLink,
  Tag,
  useIsBreakpointMediumAndUp,
  Panel,
  useIsCashTheme,
} from '@afterpaytouch/core'
import {
  ChangePaymentDate,
  PayNowModal,
  Money,
  OrderDeliveryInfoSection,
  OrderPaymentMethod,
  ProductSummary,
  useChangePaymentDateModal,
  usePayNowModal,
} from '..'
import { useFlag } from '../../hooks'
import { getMerchantName } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { isConsumerLendingOrder, useConsumerAccountIsInHardship, useGetBankAccountsQuery } from '../../state'
import { OrderSummary } from '../OrderSummary'
import { useGetOrderTransactionQuery, useHasOrderTotalOwed } from '../../state/orders/hooks'
import { useConsumerSizes } from '../../utils/responsive'
import { PayNowButton } from './PayNowButton'
import { TrackPageViewed, useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { BreadcrumbContext } from '../Breadcrumb/BreadcrumbContext'
import { Route } from '../../config/router'
import { TrackingEvent } from '../../model/amplitude'
import { OrderProgress } from '../OrderProgress'
import { OrderSnapshot } from '../OrderSnapshot'
import { AchBankAccountStatuses, PaymentMethodType, SupportedLocale } from '@afterpay/types'
import { useDisputeInfoModal, DisputeInfoModal } from '../DisputeInfoModal'
import { OrderScheduleTimeLine } from '../OrderScheduleTimeLine'
import { StoreLogo } from '../StoreLogo'
import { useSearchParam } from '../../hooks/useSearchParam/useSearchParam'
import { stringToBoolean } from '@afterpay/utils/string'
import { useHasOrderChangePaymentDateEnabled, useIsAFFOrder } from './hooks'
import { sumMoney } from '../Money/utils'

const I18N_NAMESPACE = ['common', 'index', 'orders', 'payment']

interface OrderDetailPanelProps {
  orderId: string
}

export const OrderDetailPanel: FunctionComponent<OrderDetailPanelProps> = ({ orderId }) => {
  const { setCrumbs } = useContext(BreadcrumbContext)
  const { t } = useTranslation(I18N_NAMESPACE)
  const isCashTheme = useIsCashTheme()
  const { subheadingSize } = useConsumerSizes()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const {
    data: orderTransaction,
    isFetching: isGetOrderTransactionFetching,
    isLoading: isGetOrderTransactionLoading,
  } = useGetOrderTransactionQuery(orderId, {
    refetchOnMountOrArgChange: true,
  })
  const { orderSummary, paymentMethod, paymentType, paymentScheduleSet } = orderTransaction ?? {}
  const { items } = orderTransaction?.orderDetail ?? {}
  // @ts-ignore: OPERATION BLEED STOPPER
  const orderAmount = orderSummary?.donation !== null ? sumMoney(orderSummary?.orderTotal, orderSummary?.donation?.amount) : orderSummary?.orderTotal
  const hasOrderTotalOwed = useHasOrderTotalOwed(orderId)
  const hasOrderChangePaymentDateEnabled = useHasOrderChangePaymentDateEnabled(orderId)
  const isConsumerLending = isConsumerLendingOrder(paymentType)
  const isHardship = useConsumerAccountIsInHardship()
  const isOrderDetailV2Enabled = useFlag('consumer-portal-order-detail-v2-enabled')
  const isAchFlagEnabled = useFlag('ach-eligibility-enabled')
  const { data: bankAccountData } = useGetBankAccountsQuery(undefined, {
    skip: !isAchFlagEnabled,
  })
  const isAFFOrder = useIsAFFOrder(orderId)
  const achBankAccountStatus = useMemo(
    () =>
      paymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT
        ? bankAccountData?.accounts?.find((bankAccount) => bankAccount.id === paymentMethod.id)?.status
        : undefined,
    [paymentMethod, bankAccountData]
  )

  const hasUnlinkedBankAccount = useMemo(
    () => bankAccountData?.accounts?.some((bankAccount) => bankAccount.status === AchBankAccountStatuses.UNLINKED),
    [bankAccountData]
  )
  const fallbackAmount = { amount: '0', currency: orderSummary?.orderTotal?.currency ?? 'USD' }

  const {
    isDisputed,
    isDisputeClosed,
    openModal: openDisputeInfoModal,
    consumerDisplayState,
    disputeInfo,
    closeModal: closeDisputeInfoModal,
    isModalOpen: isDisputeInfoModalOpen,
    refresh: refreshDisputeInfo,
    chargebacks,
    chargebackBannerVisible,
  } = useDisputeInfoModal(orderId)
  const isBpMediumUp = useIsBreakpointMediumAndUp()

  useEffect(() => {
    const { id, channel } = orderTransaction ?? {}
    if (Boolean(id) && Boolean(channel)) {
      // @ts-ignore: OPERATION BLEED STOPPER
      setCrumbs({ [Route.ORDER]: { orderId: id, channel } })
    }
  }, [orderTransaction, setCrumbs])

  const onChangePaymentDateModalOpen = (): void => {
    logEvent('Pressed Open Payment Reschedule Modal')
  }

  const payNowModalName = 'pay-now'
  // @ts-ignore: OPERATION BLEED STOPPER
  const isPayNowFlowModalOpenParam = stringToBoolean(useSearchParam(`modal-${payNowModalName}`))
  const [isPayNowModalOpen, { openModal: openPayNowModal, closeModal: closePayNowModal }] = usePayNowModal(isPayNowFlowModalOpenParam)
  useModalDeepLink(isPayNowModalOpen, payNowModalName)

  const [isChangePaymentDateModalOpen, { openModal: openChangePaymentDateModal, closeModal: closeChangePaymentDateModal }] = useChangePaymentDateModal(false, {
    onOpen: onChangePaymentDateModalOpen,
  })

  const labels = {
    consumerLendingWarning: 'orders:orderPaymentReschedule:consumerLendingWarning',
    changeDateText: 'orders:orderPaymentReschedule:changeDateText',
    delinkedBankAccountMsgTitle: 'orders:orderPaymentReschedule:delinkedBankAccountMsgTitle',
    newLocal: 'orders:orderPaymentReschedule:delinkedBankAccountMsg',
    pausedOrder: 'orders:orderSummary:pausedOrder',
    disputedOrder: 'orders:orderSummary:disputedOrder',
    newChargebackWarning: 'orders:orderSummary:newChargebackWarning',
    resolvedChargebackWarning: 'orders:orderSummary:resolvedChargebackWarning',
    order: 'orders:order',
  }
  const tagLabel = `${paymentScheduleSet != null ? paymentScheduleSet.length : 4} ${t('common:payments')}`

  return (
    <div>
      {Boolean(paymentType) && <TrackPageViewed eventProps={{ paymentType }} />}
      <Panel testNameSpace='order-details'>
        <Panel.Head>
          <Grid verticalGutter={{ sm: 1, md: 1 }}>
            <Grid.Item>
              <div className='relative flex flex-row'>
                <div className='mr-4'>
                  {isGetOrderTransactionFetching || isGetOrderTransactionLoading ? (
                    <div className='relative h-16 w-16'>
                      <Skeleton kind={isCashTheme ? 'Default' : 'Primary'} />
                    </div>
                  ) : (
                    // @ts-ignore: OPERATION BLEED STOPPER
                    <StoreLogo merchant={orderTransaction?.merchant} background='Alternative' size='Large' />
                  )}
                </div>
                <div>
                  {/* @ts-ignore: OPERATION BLEED STOPPER */}
                  <Heading size={subheadingSize}>{getMerchantName(orderTransaction?.merchant) ?? t(labels.order)}</Heading>
                  <Money bold value={orderAmount ?? fallbackAmount} size={subheadingSize} testNameSpace='order-amount' />
                </div>
                {isGetOrderTransactionLoading && <Skeleton kind={isCashTheme ? 'Default' : 'Primary'} />}
              </div>
            </Grid.Item>
            <Grid.Item sm={12} lg={3}>
              <div className='relative flex flex-row'>
                {hasOrderTotalOwed && !isHardship && <PayNowButton onClick={openPayNowModal} orderId={orderId} outboundLink={router.pathname} />}
                {isGetOrderTransactionLoading && <Skeleton kind={isCashTheme ? 'Default' : 'Primary'} />}
              </div>
            </Grid.Item>
          </Grid>
        </Panel.Head>
        <Panel.Content bordered>
          {hasUnlinkedBankAccount && (
            <div className='mb-3'>
              <Message
                heading={t(labels.delinkedBankAccountMsgTitle)}
                description={
                  <Text>
                    <Trans
                      i18nKey={labels.newLocal}
                      components={{
                        paymentLink: <Link href={`/${locale}${Route.ACCOUNT__PAYMENT_METHODS}`} kind='Underline' />,
                      }}
                    />
                  </Text>
                }
                iconName='Info'
                testNameSpace='unlinked-payment-method'
              />
            </div>
          )}
          {isDisputed && (
            <div className='my-4'>
              <Message
                kind={isDisputeClosed ? 'Info' : 'Success'}
                description={
                  <Text>
                    <Trans
                      i18nKey={labels.disputedOrder}
                      components={{
                        viewDetail: (
                          <Link kind='Underline' onClick={openDisputeInfoModal}>
                            <Text bold></Text>
                          </Link>
                        ),
                      }}
                      values={{
                        consumerDisplayState,
                      }}
                    />
                  </Text>
                }
                iconName='Info'
                testNameSpace='change-payment-pcl-warning'
              />
            </div>
          )}
          {chargebackBannerVisible && (
            <>
              {chargebacks.map((chargeback, index) => {
                return (
                  <div className='my-4' key={chargeback.id}>
                    <Message
                      kind={chargeback.isResolved ? 'Success' : 'Info'}
                      description={
                        <Text>
                          <Trans i18nKey={chargeback.isResolved ? labels.resolvedChargebackWarning : labels.newChargebackWarning} />
                        </Text>
                      }
                      iconName='Info'
                      testNameSpace={`chargeback-warning-${index}`}
                    />
                  </div>
                )
              })}
            </>
          )}
          {isConsumerLending && (
            <>
              <div className='mb-3 lg:mb-6'>
                <Grid center gutter={{ lg: 5 }} verticalGutter={{ sm: 1, md: 1 }}>
                  <Grid.Item sm={12} lg={6}>
                    <OrderSnapshot orderId={orderId} />
                  </Grid.Item>
                  <Grid.Item sm={12} lg={6}>
                    <OrderProgress orderId={orderId} />
                  </Grid.Item>
                </Grid>
              </div>
              <div className='hidden md:block'>
                <Divider kind='Hairline' margin='Large' />
              </div>
            </>
          )}
          {isHardship && (
            <div className='my-4'>
              <Message
                kind='Info'
                iconName='Warning'
                description={
                  <Text>
                    <Trans
                      i18nKey={labels.pausedOrder}
                      components={{
                        hardshipLink: <Link kind='Underline' href={`/${locale + Route.UPCOMING_PAYMENTS}`}></Link>,
                      }}
                    />
                  </Text>
                }
              />
            </div>
          )}

          <div className='grid gap-x-20 gap-y-8 lg:grid-cols-2 lg:gap-y-0'>
            <div>
              {isOrderDetailV2Enabled && !isConsumerLending && isBpMediumUp && (
                <div className='mb-4'>
                  <Tag.Primary testNameSpace='payment-tag'>{tagLabel}</Tag.Primary>
                </div>
              )}
              <OrderSummary orderId={orderId} />
              <div className='pt-8'>
                {/* TODO: Remove canPaymentMethodChange when risk rules change */}
                <OrderPaymentMethod
                  // @ts-ignore: OPERATION BLEED STOPPER
                  paymentData={paymentMethod}
                  orderId={orderId}
                  canPaymentMethodChange={orderTransaction?.paymentMethodChangeAllowed && !isHardship}
                  displayStorePaymentToken={false}
                  achBankAccountStatus={achBankAccountStatus}
                />
              </div>
              <div className='pt-8'>
                {/* @ts-ignore: OPERATION BLEED STOPPER */}
                <ProductSummary itemDetails={items} />
              </div>
            </div>
            <div className='order-first lg:order-none'>
              <div className='sticky top-6'>
                {isOrderDetailV2Enabled && !isConsumerLending && (
                  <>
                    {!isBpMediumUp && (
                      <div className='mb-4 flex flex-row items-center justify-between'>
                        <Heading size={'L'}>{t('orders:orderSchedule:schedule')}</Heading>
                        <div>
                          <Tag.Primary testNameSpace='payment-tag'>{tagLabel}</Tag.Primary>
                        </div>
                      </div>
                    )}

                    <div className='mb-4'>
                      <OrderProgress orderId={orderId} />
                    </div>
                  </>
                )}
                <OrderScheduleTimeLine orderId={orderId} />
                {isConsumerLending && (
                  <div className='my-4'>
                    <Message
                      kind='Basic'
                      description={
                        <Text>
                          <Trans
                            i18nKey={labels.consumerLendingWarning}
                            components={{
                              paymentLink: <Link href={`/${locale}${Route.ACCOUNT__PAYMENT_METHODS}`} kind='Underline' />,
                            }}
                          />
                        </Text>
                      }
                      iconName='Info'
                      testNameSpace='change-payment-pcl-warning'
                    />
                  </div>
                )}
                {hasOrderChangePaymentDateEnabled && (
                  <div className='mt-6'>
                    <Button.Ghost
                      padding={isBpMediumUp ? 'Fixed' : 'Fluid'}
                      onClick={() => {
                        openChangePaymentDateModal()
                        logEvent(TrackingEvent.CLICKED_CHANGE_PAYMENT_DATE, {
                          orderId,
                        })
                      }}
                      testNameSpace='change-payment-date-open-modal'
                    >
                      {t(labels.changeDateText)}
                    </Button.Ghost>
                  </div>
                )}
              </div>
              {orderTransaction !== null && isChangePaymentDateModalOpen && (
                // @ts-ignore: OPERATION BLEED STOPPER
                <ChangePaymentDate order={orderTransaction} locale={locale} closeModal={closeChangePaymentDateModal} />
              )}
            </div>
          </div>
          <Divider kind='Hairline' margin='Large' />
          <OrderDeliveryInfoSection id={orderId} refreshDisputeInfo={refreshDisputeInfo} />
        </Panel.Content>
      </Panel>
      <PayNowModal
        initalStep={orderTransaction?.clearpayPartiallyMigrated ? 'ClearpayPartiallyMigratedRedirection' : undefined}
        order={{ orderId, orderAmount }}
        isModalOpen={isPayNowModalOpen}
        closeModal={closePayNowModal}
        achFlowEntryDeepLink={`/orders/${orderId}/${Route.ORDER__PAYNOW_ADDBANKACCOUNTMODAL?.match(/(\?).*/g)?.[0]}`}
      />
      <DisputeInfoModal
        disputeInfo={disputeInfo}
        isModalOpen={isDisputeInfoModalOpen}
        closeModal={closeDisputeInfoModal}
        // @ts-ignore: OPERATION BLEED STOPPER
        order={orderTransaction}
        refresh={refreshDisputeInfo}
      />
    </div>
  )
}
